// V·IAnet Governance — AdminPuestos (migrado al shell · prompt 0471 · M5)
// Listado de puestos. Click en fila abre AdminPuestoPanel unificado (4 tabs).

import { useState, useEffect, useCallback } from 'react';
import {
  fetchPuestos,
  fetchDepartamentos,
  fetchOrganizaciones,
  fetchNivelesPuesto,
  actualizarPuesto,
  eliminarPuesto,
  type NivelPuesto,
} from '../../../../shared/services/api';
import { useI18n } from '../../../../../i18n';
import { AdminPuestoPanel, type AdminPuestoPanelInput } from './AdminPuestoPanel';
import { NIVEL_FALLBACK } from '../../../../../lib/nivel-style';
import {
  AdminPageHeader,
  AdminTableShell,
  AdminActionButton,
  type AdminTableColumn,
} from '../../../../shared/components/admin';

// 0479 M2 — Iconos canonicos (editar · toggle · borrar) homogeneos con RF/RG
function EditIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true">
      <path
        d="M8.5 1.5L10.5 3.5L4 10L1.5 10.5L2 8L8.5 1.5Z"
        stroke="currentColor"
        strokeWidth="1.3"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}
function TrashIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true">
      <path
        d="M2 3H10M4.5 3V2C4.5 1.5 4.8 1 5.5 1H6.5C7.2 1 7.5 1.5 7.5 2V3M3 3L3.5 10.5C3.5 10.8 3.8 11 4 11H8C8.2 11 8.5 10.8 8.5 10.5L9 3"
        stroke="currentColor"
        strokeWidth="1.3"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}
function PowerIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true">
      <path
        d="M4 2.5C2.8 3.2 2 4.5 2 6c0 2.2 1.8 4 4 4s4-1.8 4-4c0-1.5-.8-2.8-2-3.5M6 1.5V6"
        stroke="currentColor"
        strokeWidth="1.3"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

interface PuestoAdminItem {
  id: string;
  titulo: string;
  codigo?: string;
  nivel: string;
  nivelPuesto?: { nombre?: string; color?: string | null; colorFondo?: string | null } | null;
  departamentoId?: string;
  organizacionId?: string;
  departamento?: { nombre: string } | null;
  organizacion?: { nombre: string } | null;
  rolFuncionalId?: string | null;
  nivelPuestoId?: string | null;
  fichaDocumentoId?: string | null;
  activo?: boolean;
  _count?: { asignaciones: number };
}

interface SelectOption {
  value: string;
  label: string;
  orgId?: string;
}

function NivelBadge({
  nivel,
  nivelPuesto,
}: {
  nivel: string;
  nivelPuesto?: { nombre?: string; color?: string | null; colorFondo?: string | null } | null;
}) {
  const np = nivelPuesto;
  const color = np?.color ?? NIVEL_FALLBACK.color;
  const bg = np?.colorFondo ?? NIVEL_FALLBACK.bg;
  const label = np?.nombre ?? nivel;
  return (
    <span
      style={{
        display: 'inline-block',
        padding: '2px 8px',
        borderRadius: 10,
        fontSize: 11,
        fontWeight: 600,
        background: bg,
        color,
        whiteSpace: 'nowrap',
      }}
    >
      {label}
    </span>
  );
}

// 0453: "1 puesto = 1 asignacion activa max". Badge binario vacante/ocupado.
function EstadoOcupacionBadge({ ocupados }: { ocupados: number }) {
  const { t } = useI18n();
  const adm = (t.structure as Record<string, unknown>)?.admin as
    | Record<string, unknown>
    | undefined;
  const lbl = (adm ?? {}) as Record<string, string>;
  const vacante = ocupados === 0;
  const text = vacante ? (lbl.estadoVacante ?? 'Vacante') : (lbl.estadoOcupado ?? 'Ocupado');
  return (
    <span
      style={{
        display: 'inline-block',
        padding: '2px 8px',
        borderRadius: 10,
        fontSize: 11,
        fontWeight: 600,
        background: vacante ? '#ECFDF5' : '#F1F5F9',
        color: vacante ? '#16A34A' : '#5A6171',
        whiteSpace: 'nowrap',
      }}
    >
      {text}
    </span>
  );
}

// ─── Component ───────────────────────────────────────────────

export function AdminPuestos() {
  const { t } = useI18n();
  const adm = (t.structure as Record<string, unknown>)?.admin as
    | Record<string, unknown>
    | undefined;
  const lbl = (adm ?? {}) as Record<string, string>;
  const pag = t.common.pagination;

  // Tooltip didactico — usa t.structure.tooltips.puestos si existe
  const tooltips = (t.structure as Record<string, unknown>)?.tooltips as
    | Record<string, { titulo?: string; ayuda?: string }>
    | undefined;
  const tooltipPuestos = tooltips?.adminPuestos ?? tooltips?.puestos;

  const [puestos, setPuestos] = useState<PuestoAdminItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [refreshKey, setRefreshKey] = useState(0);
  const [filterNivel, setFilterNivel] = useState('');
  const [filterDepto, setFilterDepto] = useState('');
  const [mostrarInactivos, setMostrarInactivos] = useState(false);
  const [nivelesPuesto, setNivelesPuesto] = useState<NivelPuesto[]>([]);

  // 'new' = panel en modo creacion · objeto = panel en modo edicion · null = cerrado
  const [panelPuesto, setPanelPuesto] = useState<PuestoAdminItem | 'new' | null>(null);
  const [toast, setToast] = useState('');

  const [deptos, setDeptos] = useState<SelectOption[]>([]);

  useEffect(() => {
    fetchNivelesPuesto()
      .then((r) => setNivelesPuesto(r.data ?? []))
      .catch(() => {});
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const params: Record<string, string> = { pagina: String(page), porPagina: '100' };
        if (debouncedSearch) params.q = debouncedSearch;
        const res = await fetchPuestos(params);
        if (cancelled) return;
        setPuestos(
          ((res as unknown as { datos?: PuestoAdminItem[] }).datos ??
            (res.data as unknown as PuestoAdminItem[]) ??
            []) as PuestoAdminItem[],
        );
        const meta = res.meta;
        setTotal(meta?.total ?? 0);
        setTotalPages(meta?.totalPages ?? Math.ceil((meta?.total ?? 0) / 100));
      } catch {
        if (!cancelled) setPuestos([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [page, debouncedSearch, refreshKey]);

  const loadPuestos = useCallback(() => {
    setRefreshKey((k) => k + 1);
  }, []);

  useEffect(() => {
    fetchDepartamentos({ porPagina: '100' })
      .then((r) =>
        setDeptos(
          (r.data ?? []).map((d) => ({ value: d.id, label: d.nombre, orgId: d.organizacionId })),
        ),
      )
      .catch(() => {});
    fetchOrganizaciones().catch(() => {});
  }, []);

  const filtered = puestos.filter((p) => {
    if (!mostrarInactivos && p.activo === false) return false;
    if (filterNivel && p.nivel !== filterNivel) return false;
    if (filterDepto && p.departamentoId !== filterDepto) return false;
    return true;
  });

  const showToast = (msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(''), 3000);
  };

  // 0479 M2 — toggle activo + borrar con confirmacion canonica (patron RF/RG)
  const handleToggleActivo = async (p: PuestoAdminItem) => {
    try {
      await actualizarPuesto(p.id, { activo: !(p.activo ?? true) } as Record<string, unknown>);
      loadPuestos();
      showToast(p.activo === false ? 'Puesto activado' : 'Puesto desactivado');
    } catch {
      showToast('Error al cambiar estado');
    }
  };

  const handleBorrar = async (p: PuestoAdminItem) => {
    const asignados = p._count?.asignaciones ?? 0;
    const msg =
      asignados > 0
        ? `Este puesto tiene ${asignados} asignacion(es) activa(s). Desactivalas primero para poder eliminar.`
        : 'Eliminar este puesto?';
    if (asignados > 0) {
      showToast(msg);
      return;
    }
    if (!window.confirm(msg)) return;
    try {
      await eliminarPuesto(p.id);
      loadPuestos();
      showToast('Puesto eliminado');
    } catch {
      showToast('Error al eliminar');
    }
  };

  const nivelOptions = nivelesPuesto.map((n) => n.nombre);

  const panelInput: AdminPuestoPanelInput | null =
    panelPuesto === null
      ? null
      : panelPuesto === 'new'
        ? null
        : {
            id: panelPuesto.id,
            titulo: panelPuesto.titulo,
            codigo: panelPuesto.codigo,
            nivelPuestoId: panelPuesto.nivelPuestoId ?? null,
            organizacionId: panelPuesto.organizacionId ?? null,
            departamentoId: panelPuesto.departamentoId ?? null,
            rolFuncionalId: panelPuesto.rolFuncionalId ?? null,
            fichaDocumentoId: panelPuesto.fichaDocumentoId ?? null,
          };

  const columns: AdminTableColumn<PuestoAdminItem>[] = [
    {
      key: 'codigo',
      label: lbl.colCodigo ?? 'Codigo',
      mono: true,
      sortable: true,
      sortKey: (r) => r.codigo ?? '',
      render: (r) => <span>{r.codigo ?? '—'}</span>,
    },
    {
      key: 'titulo',
      label: lbl.colTitulo ?? 'Titulo',
      sortable: true,
      sortKey: (r) => r.titulo ?? '',
      render: (r) => <span>{r.titulo ?? '—'}</span>,
    },
    {
      key: 'nivel',
      label: lbl.colNivel ?? 'Nivel',
      render: (r) => <NivelBadge nivel={r.nivel ?? ''} nivelPuesto={r.nivelPuesto} />,
    },
    {
      key: 'departamento',
      label: lbl.colDepartamento ?? 'Departamento',
      sortable: true,
      sortKey: (r) => r.departamento?.nombre ?? '',
      render: (r) => <span style={{ fontSize: 12 }}>{r.departamento?.nombre ?? '—'}</span>,
    },
    {
      key: 'organizacion',
      label: lbl.colOrganizacion ?? 'Organizacion',
      sortable: true,
      sortKey: (r) => r.organizacion?.nombre ?? '',
      render: (r) => <span style={{ fontSize: 12 }}>{r.organizacion?.nombre ?? '—'}</span>,
    },
    {
      key: 'estado',
      label: lbl.colEstado ?? 'Estado',
      render: (r) => <EstadoOcupacionBadge ocupados={r._count?.asignaciones ?? 0} />,
    },
    // 0479 M2 — columna Activo canonica (pill pasiva, boton toggle en Acciones)
    {
      key: 'activo',
      label: lbl.activo ?? 'Activo',
      align: 'center',
      render: (r) => (
        <span
          style={{
            display: 'inline-block',
            padding: '2px 10px',
            borderRadius: 10,
            fontSize: 11,
            fontWeight: 600,
            background: r.activo !== false ? '#D1FAE5' : '#FEE2E2',
            color: r.activo !== false ? '#065F46' : '#991B1B',
            whiteSpace: 'nowrap',
          }}
        >
          {r.activo !== false ? (lbl.activo ?? 'Activo') : (lbl.inactivo ?? 'Inactivo')}
        </span>
      ),
    },
    // 0479 M2 — columna Ficha FP canonica (patron RF: Crear/Ver)
    {
      key: 'ficha',
      label: lbl.fichaFP ?? 'Ficha FP',
      align: 'center',
      render: (r) => (
        <button
          onClick={(e) => {
            e.stopPropagation();
            setPanelPuesto(r);
          }}
          style={{
            background: 'none',
            border: '1px solid #D1D5DB',
            padding: '3px 10px',
            borderRadius: 6,
            fontSize: 11,
            cursor: 'pointer',
            fontFamily: 'inherit',
            color: r.fichaDocumentoId ? '#1F3864' : '#6B7280',
          }}
        >
          {r.fichaDocumentoId ? (lbl.fichaVer ?? 'Ver FP') : (lbl.fichaCrear ?? 'Crear FP')}
        </button>
      ),
    },
  ];

  // Filtros: dropdowns de nivel + departamento
  const filtersNode = (
    <>
      <select
        value={filterNivel}
        onChange={(e) => setFilterNivel(e.target.value)}
        style={filterSelectStyle}
      >
        <option value="">{lbl.todosLosNiveles ?? 'Todos los niveles'}</option>
        {nivelOptions.map((n) => (
          <option key={n} value={n}>
            {n}
          </option>
        ))}
      </select>
      <select
        value={filterDepto}
        onChange={(e) => setFilterDepto(e.target.value)}
        style={filterSelectStyle}
      >
        <option value="">{lbl.todosLosDepartamentos ?? 'Todos los departamentos'}</option>
        {deptos.map((d) => (
          <option key={d.value} value={d.value}>
            {d.label}
          </option>
        ))}
      </select>
    </>
  );

  return (
    <div>
      <AdminPageHeader
        title={lbl.puestosTitle ?? lbl.puestos ?? 'Puestos'}
        infoTooltip={
          tooltipPuestos?.ayuda
            ? {
                title: tooltipPuestos.titulo ?? lbl.puestosTitle ?? 'Puestos',
                text: tooltipPuestos.ayuda,
              }
            : {
                title: 'Catálogo de puestos',
                text: 'Listado de todos los puestos definidos en la organización.\n\nCada puesto pertenece a un rol funcional y puede tener una o varias personas asignadas. Los puestos son la unidad de asignación de responsabilidades en procesos y documentos.',
              }
        }
        search={{
          value: search,
          onChange: setSearch,
          placeholder: lbl.search ?? 'Buscar...',
        }}
        filters={filtersNode}
        extras={
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
            <input
              type="checkbox"
              checked={mostrarInactivos}
              onChange={(e) => setMostrarInactivos(e.target.checked)}
            />
            {lbl.mostrarInactivos ?? 'Mostrar inactivos'}
          </label>
        }
        onNew={{
          label: lbl.newPuesto ?? 'Nuevo puesto',
          onClick: () => setPanelPuesto('new'),
        }}
      />

      {toast && (
        <div
          style={{
            position: 'fixed',
            bottom: 24,
            right: 24,
            zIndex: 200,
            background: '#0E243E',
            color: '#fff',
            padding: '10px 20px',
            borderRadius: 8,
            fontSize: 13,
            fontWeight: 500,
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
          }}
        >
          {toast}
        </div>
      )}

      <AdminTableShell
        columns={columns}
        rows={filtered}
        rowKey={(p) => p.id}
        onRowClick={(p) => setPanelPuesto(p)}
        loading={loading}
        loadingMessage={t.common.cargando ?? 'Cargando…'}
        emptyMessage={lbl.noResults ?? 'Sin resultados'}
        actions={(p) => (
          <>
            <AdminActionButton
              icon={<EditIcon />}
              label="Editar"
              onClick={() => setPanelPuesto(p)}
            />
            <AdminActionButton
              icon={<PowerIcon />}
              label={p.activo === false ? 'Activar' : 'Desactivar'}
              onClick={() => handleToggleActivo(p)}
            />
            <AdminActionButton
              icon={<TrashIcon />}
              label="Eliminar"
              variant="danger"
              onClick={() => handleBorrar(p)}
            />
          </>
        )}
        pagination={
          totalPages > 1
            ? {
                page,
                pageSize: 100,
                total,
                onPageChange: setPage,
                labels: {
                  prev: pag.anterior,
                  next: pag.siguiente,
                  pageOf: pag.pageOf,
                  totalCount: (n) => `${n} ${lbl.countPuestos ?? 'puestos'}`,
                },
              }
            : undefined
        }
        footer={
          !loading && total > 0 && totalPages <= 1 ? (
            <span>
              {Math.min((page - 1) * 100 + 1, total)}–{Math.min(page * 100, total)} de {total}{' '}
              {lbl.countPuestos ?? 'puestos'}
            </span>
          ) : undefined
        }
      />

      {/* Panel unificado con 4 tabs */}
      {panelPuesto !== null && (
        <AdminPuestoPanel
          puesto={panelInput}
          onClose={() => setPanelPuesto(null)}
          onSaved={() => {
            loadPuestos();
            showToast(lbl.saved ?? 'Guardado correctamente');
          }}
        />
      )}
    </div>
  );
}

// ─── Styles ──────────────────────────────────────────────────

const filterSelectStyle: React.CSSProperties = {
  padding: '7px 10px',
  border: '1px solid #D1D5DB',
  borderRadius: 6,
  fontSize: 13,
  background: '#fff',
  outline: 'none',
};
